import { Volume2 } from "lucide-react";
import { useMusic } from "@/context/MusicContext";

// Deterministic, gentle floating gold particles.
const PARTICLES = Array.from({ length: 16 }).map((_, i) => {
  const seed = (i * 97) % 100;
  return {
    left: 4 + ((i * 61) % 92),
    top: 6 + ((i * 37) % 88),
    size: 2 + (seed % 4),
    delay: -(i * 0.9),
    dur: 12 + (seed % 9),
    op: 0.25 + ((seed % 5) * 0.12),
  };
});

// Premium cinematic entrance shown BEFORE the existing Sisterhood+ splash.
export default function EnterScreen({ onEnter }) {
  const music = useMusic();
  const handleEnter = () => {
    try { music && music.enableWithSound && music.enableWithSound(); } catch (e) { /* noop */ }
    onEnter();
  };

  return (
    <div data-testid="enter-screen" className="es-root">
      <style>{CSS}</style>

      {/* Animated luxury background */}
      <div className="es-bg" aria-hidden="true">
        <div className="es-silk es-silk--1" />
        <div className="es-silk es-silk--2" />
        <div className="es-silk es-silk--3" />
        <div className="es-shimmer" />
        <div className="es-vignette" />
        <div className="es-particles">
          {PARTICLES.map((p, i) => (
            <span key={i} style={{
              left: `${p.left}%`, top: `${p.top}%`, width: p.size, height: p.size,
              animationDelay: `${p.delay}s`, animationDuration: `${p.dur}s`, opacity: p.op,
            }} />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="es-content">
        <div className="es-logo">
          <span className="es-word">Sisterhood<span className="es-plus">+</span></span>
        </div>
        <p className="es-tag">Your space. Your journey. Your sisterhood.</p>

        <button data-testid="enter-button" onClick={handleEnter} aria-label="Enter Sisterhood+" className="es-btn">
          <span className="es-btn__glow" aria-hidden="true" />
          <span className="es-btn__icon"><Volume2 size={16} strokeWidth={2} /></span>
          <span className="es-btn__label">Enter</span>
        </button>
      </div>
    </div>
  );
}

const CSS = `
.es-root{position:fixed;inset:0;z-index:10000;overflow:hidden;background:#1B1020;display:flex;align-items:center;justify-content:center;-webkit-font-smoothing:antialiased;}

/* ---- Background layers ---- */
.es-bg{position:absolute;inset:0;z-index:0;}
.es-silk{position:absolute;border-radius:50%;filter:blur(70px);opacity:.55;mix-blend-mode:screen;will-change:transform;}
.es-silk--1{width:70vmax;height:70vmax;left:-18vmax;top:-22vmax;background:radial-gradient(circle at 30% 30%, #6E3B6B 0%, rgba(110,59,107,0) 62%);animation:esDrift1 22s ease-in-out infinite alternate;}
.es-silk--2{width:66vmax;height:66vmax;right:-20vmax;top:-8vmax;background:radial-gradient(circle at 60% 40%, #3B1E52 0%, rgba(59,30,82,0) 60%);animation:esDrift2 27s ease-in-out infinite alternate;}
.es-silk--3{width:80vmax;height:80vmax;left:50%;bottom:-42vmax;transform:translateX(-50%);background:radial-gradient(circle at 50% 40%, #4A2140 0%, rgba(74,33,64,0) 58%);animation:esDrift3 31s ease-in-out infinite alternate;}
.es-shimmer{position:absolute;inset:-20% -40%;background:linear-gradient(115deg,transparent 38%,rgba(200,155,85,0.10) 48%,rgba(255,231,175,0.16) 50%,rgba(200,155,85,0.10) 52%,transparent 62%);transform:translateX(-30%);animation:esShimmer 12s ease-in-out infinite;}
.es-vignette{position:absolute;inset:0;background:radial-gradient(120% 90% at 50% 42%, rgba(27,16,32,0) 40%, rgba(15,9,18,0.55) 78%, rgba(12,7,14,0.85) 100%);}
.es-particles span{position:absolute;border-radius:999px;background:radial-gradient(circle,#FFE7AF 0%,rgba(200,155,85,0.6) 60%,rgba(200,155,85,0) 100%);box-shadow:0 0 10px rgba(255,231,175,0.55);animation-name:esFloat;animation-timing-function:ease-in-out;animation-iteration-count:infinite;}

/* ---- Content ---- */
.es-content{position:relative;z-index:1;display:flex;flex-direction:column;align-items:center;text-align:center;padding:32px;}
.es-logo{opacity:0;animation:esFade 1.2s cubic-bezier(.22,1,.36,1) .15s both;}
.es-word{font-family:'Outfit',sans-serif;font-weight:600;letter-spacing:-.5px;color:#FCFAF6;font-size:clamp(38px,7vw,64px);line-height:1;text-shadow:0 2px 30px rgba(0,0,0,0.35);}
.es-plus{color:#C89B55;}
.es-tag{margin-top:22px;color:rgba(252,250,246,.62);font-family:'Outfit',sans-serif;font-weight:500;text-transform:uppercase;letter-spacing:.42em;font-size:clamp(9px,1.4vw,12px);opacity:0;animation:esFade 1.2s cubic-bezier(.22,1,.36,1) .6s both;}

/* ---- Button ---- */
.es-btn{position:relative;margin-top:clamp(40px,7vh,72px);display:inline-flex;align-items:center;gap:12px;padding:18px 46px;border-radius:999px;cursor:pointer;color:#FCFAF6;font-family:'Outfit',sans-serif;font-weight:600;font-size:14px;letter-spacing:.32em;text-transform:uppercase;background:linear-gradient(180deg,rgba(255,255,255,0.14),rgba(255,255,255,0.05));border:1px solid rgba(200,155,85,0.55);backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);box-shadow:0 10px 40px rgba(0,0,0,0.35),0 0 0 1px rgba(255,255,255,0.04) inset,0 0 28px rgba(200,155,85,0.18);opacity:0;transform:translateY(14px);animation:esRise 1s cubic-bezier(.22,1,.36,1) 1s both, esBtnGlow 5.5s ease-in-out 2.4s infinite;transition:transform .35s cubic-bezier(.22,1,.36,1),box-shadow .35s ease,border-color .35s ease,background .35s ease;}
.es-btn:hover{transform:translateY(-2px) scale(1.02);border-color:rgba(200,155,85,0.9);background:linear-gradient(180deg,rgba(255,255,255,0.2),rgba(255,255,255,0.07));box-shadow:0 16px 50px rgba(0,0,0,0.42),0 0 40px rgba(200,155,85,0.4);}
.es-btn:active{transform:translateY(0) scale(.985);transition-duration:.08s;}
.es-btn__glow{position:absolute;inset:-2px;border-radius:999px;background:radial-gradient(120% 140% at 50% 120%, rgba(200,155,85,0.35), rgba(200,155,85,0) 60%);pointer-events:none;}
.es-btn__icon{display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:999px;background:rgba(200,155,85,0.16);color:#E7C98F;}
.es-btn__label{padding-right:2px;}

/* ---- Keyframes ---- */
@keyframes esDrift1{0%{transform:translate(0,0) scale(1);}100%{transform:translate(6vmax,4vmax) scale(1.08);}}
@keyframes esDrift2{0%{transform:translate(0,0) scale(1.05);}100%{transform:translate(-5vmax,5vmax) scale(1);}}
@keyframes esDrift3{0%{transform:translateX(-50%) translateY(0) scale(1);}100%{transform:translateX(-50%) translateY(-5vmax) scale(1.06);}}
@keyframes esShimmer{0%{transform:translateX(-35%);opacity:.5;}50%{transform:translateX(20%);opacity:1;}100%{transform:translateX(-35%);opacity:.5;}}
@keyframes esFloat{0%{transform:translateY(0) translateX(0);}50%{transform:translateY(-26px) translateX(10px);}100%{transform:translateY(0) translateX(0);}}
@keyframes esFade{0%{opacity:0;transform:translateY(10px);}100%{opacity:1;transform:translateY(0);}}
@keyframes esRise{0%{opacity:0;transform:translateY(16px);}100%{opacity:1;transform:translateY(0);}}
@keyframes esBtnGlow{0%,100%{box-shadow:0 10px 40px rgba(0,0,0,0.35),0 0 0 1px rgba(255,255,255,0.04) inset,0 0 24px rgba(200,155,85,0.16);}50%{box-shadow:0 14px 46px rgba(0,0,0,0.4),0 0 0 1px rgba(255,255,255,0.05) inset,0 0 44px rgba(200,155,85,0.4);}}

@media (prefers-reduced-motion:reduce){
  .es-silk--1,.es-silk--2,.es-silk--3,.es-shimmer,.es-particles span{animation:none;}
  .es-logo,.es-tag,.es-btn{animation-duration:.5s;}
  .es-btn{animation:esRise .5s ease both;}
}
`;
